import { ArrowRight, MessageCircle, ShieldCheck, Clock3 } from "lucide-react";
import { CLINIC, IMAGES, whatsappUrl } from "@/lib/clinic";

export default function Hero() {
  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative pt-32 pb-20 md:pt-40 md:pb-28 overflow-hidden"
    >
      {/* Decorative blobs */}
      <div className="pointer-events-none absolute -top-24 -right-24 w-[28rem] h-[28rem] rounded-full bg-turquoise-100/70 blur-3xl" aria-hidden="true" />
      <div className="pointer-events-none absolute top-40 -left-32 w-[22rem] h-[22rem] rounded-full bg-cream-darker blur-3xl" aria-hidden="true" />

      <div className="section-container grid lg:grid-cols-12 gap-12 items-center relative">
        <div className="lg:col-span-6 animate-fade-up">
          <span className="inline-flex items-center gap-2 text-[11px] tracking-[0.24em] uppercase text-turquoise-700 font-semibold">
            <span className="w-6 h-px bg-turquoise-600" />
            Oftalmólogos en Paraná · Entre Ríos
          </span>

          <h1
            data-testid="hero-title"
            className="mt-6 font-display text-4xl sm:text-5xl lg:text-[4rem] leading-[1.05] tracking-tight font-medium text-navy-900"
          >
            Tu visión,<br />
            <span className="italic font-light text-turquoise-700">
              cuidada con calidez
            </span>{" "}
            y precisión.
          </h1>

          <p className="mt-7 text-base sm:text-lg text-navy-800/70 max-w-xl leading-relaxed">
            Instituto oftalmológico en <strong className="font-semibold text-navy-900">Paraná, Entre Ríos</strong>. Más de una década acompañando a pacientes de la región en el diagnóstico y tratamiento de <strong className="font-semibold text-navy-900">cataratas</strong>, <strong className="font-semibold text-navy-900">presbicia</strong> y <strong className="font-semibold text-navy-900">lentes intraoculares</strong> con tecnología de última generación.
          </p>

          <div className="mt-9 flex flex-wrap items-center gap-4">
            <a
              href={whatsappUrl()}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-whatsapp-cta"
              className="group inline-flex items-center gap-3 rounded-full bg-turquoise-500 hover:bg-turquoise-600 text-navy-900 px-7 py-4 text-base font-semibold transition-all duration-300 shadow-[0_10px_30px_-10px_rgba(43,181,154,0.6)] hover:-translate-y-0.5"
            >
              <MessageCircle className="w-5 h-5" strokeWidth={2.2} />
              Agendá tu turno por WhatsApp
              <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
            </a>
            <a
              href="#patologias"
              data-testid="hero-secondary-cta"
              className="inline-flex items-center gap-2 text-sm font-medium text-navy-800 hover:text-turquoise-700 underline underline-offset-[6px] decoration-turquoise-400/60 decoration-2"
            >
              Conocé nuestras especialidades
            </a>
          </div>

          <div className="mt-10 flex flex-wrap gap-6 text-sm text-navy-800/70">
            <span className="inline-flex items-center gap-2">
              <ShieldCheck className="w-4 h-4 text-turquoise-600" /> Atendemos las principales obras sociales
            </span>
            <span className="inline-flex items-center gap-2">
              <Clock3 className="w-4 h-4 text-turquoise-600" /> {CLINIC.schedule}
            </span>
          </div>
        </div>

        <div className="lg:col-span-6 relative animate-fade-up" style={{ animationDelay: "160ms" }}>
          <div className="relative rounded-[2.25rem] overflow-hidden shadow-[0_30px_80px_-30px_rgba(11,27,61,0.25)]">
            <img
              src={IMAGES.happySeniors}
              alt="Pareja con visión clara al aire libre tras su consulta en Salud Ocular Paraná"
              className="w-full h-[460px] sm:h-[560px] object-cover"
              loading="eager"
            />
            <div className="absolute inset-0 bg-gradient-to-tr from-navy-900/25 via-transparent to-transparent" />
          </div>

          {/* Floating glass card */}
          <div
            data-testid="hero-floating-card"
            className="hidden sm:flex absolute -bottom-8 -left-6 md:-left-10 w-[280px] items-center gap-4 rounded-2xl bg-white/85 backdrop-blur-xl border border-white p-4 shadow-[0_20px_60px_-20px_rgba(11,27,61,0.25)] animate-float"
          >
            <div className="w-12 h-12 rounded-full bg-turquoise-100 flex items-center justify-center">
              <span className="font-display text-lg font-semibold text-turquoise-700">+10</span>
            </div>
            <div className="leading-tight">
              <p className="text-sm font-semibold text-navy-900">años acompañando</p>
              <p className="text-xs text-navy-700/70">la salud visual de Entre Ríos</p>
            </div>
          </div>

          {/* Top right chip */}
          <div className="hidden md:flex absolute -top-4 right-4 items-center gap-2 rounded-full bg-navy-900 text-cream px-4 py-2 text-xs font-medium shadow-lg">
            <span className="w-1.5 h-1.5 rounded-full bg-turquoise-400 animate-pulse" />
            Turnos disponibles esta semana
          </div>
        </div>
      </div>
    </section>
  );
}
